import { useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { SkeletonList } from "@/components/Skeletons";
import { Avatar } from "@/components/Avatar";
import { relativeDate } from "@/lib/format";
import { toast } from "sonner";
import client from "@/lib/api";

export default function AdminUsers() {
    const [items, setItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState(null);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const pageSize = 15;

    const load = useCallback(async () => {
        setItems(null);
        const { data } = await client.get("/admin/users", { params: { page, page_size: pageSize, q } });
        setItems(data.items);
        setTotal(data.total);
    }, [page, q]);

    useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);
    useEffect(() => { client.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

    const togglePlan = async (u) => {
        const plan = u.plan === "premium" ? "free" : "premium";
        try {
            await client.patch(`/admin/users/${u.id}/plan`, { plan });
            toast.success(`${u.name} ahora es ${plan === "premium" ? "de pago" : "gratuito"}.`);
            load();
        } catch { toast.error("No se pudo cambiar el plan."); }
    };

    const pages = Math.ceil(total / pageSize) || 1;

    return (
        <>
            <PageHeader title="Usuarios" subtitle="Todas las cuentas registradas y lo que ha creado cada una." />

            {stats && (
                <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {[["Usuarios", stats.users], ["De pago", stats.premium], ["Análisis", stats.analyses], ["Desbloqueos", stats.paid]].map(([l, v]) => (
                        <div key={l} className="rounded-lg border border-line bg-card p-4">
                            <p className="text-[13px] text-subtle">{l}</p>
                            <p className="tnum mt-1 text-[24px] font-bold text-ink">{v}</p>
                        </div>
                    ))}
                </div>
            )}

            <Input placeholder="Buscar por nombre o correo…" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}
                className="mb-5 max-w-xs" data-testid="admin-users-search" />

            {items === null ? <SkeletonList rows={6} /> : (
                <div className="space-y-4">
                    <div className="overflow-hidden rounded-lg border border-line bg-card">
                        {items.map((u) => (
                            <div key={u.id} className="flex items-center gap-4 border-b border-line px-4 py-3 last:border-0"
                                data-testid={`admin-user-${u.id}`}>
                                <Avatar name={u.name} size={40} seed={u.email} />
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-[15px] font-medium text-ink">
                                        {u.name} {u.role === "admin" && <span className="text-[12px] text-terracotta">· admin</span>}
                                        {u.deleted && <span className="text-[12px] text-destructive"> · eliminado</span>}
                                    </p>
                                    <p className="truncate text-[13px] text-subtle">{u.email}</p>
                                </div>
                                <div className="hidden text-right text-[13px] text-subtle sm:block">
                                    <p className="tnum">{u.analyses} análisis · {u.products} productos</p>
                                    <p>{relativeDate(u.created_at)}</p>
                                </div>
                                <span className={`rounded-full border px-2.5 py-0.5 text-[12px] font-medium ${u.plan === "premium" ? "border-terracotta/30 bg-terracotta/10 text-terracotta" : "border-line bg-background text-subtle"}`}>
                                    {u.plan === "premium" ? "De pago" : "Gratuito"}
                                </span>
                                {u.role !== "admin" && !u.deleted && (
                                    <Button variant="outline" size="sm" onClick={() => togglePlan(u)} data-testid={`admin-toggle-plan-${u.id}`}>
                                        {u.plan === "premium" ? "Pasar a gratuito" : "Pasar a pago"}
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                    {pages > 1 && (
                        <div className="flex items-center justify-between">
                            <span className="text-[13px] text-subtle">Página {page} de {pages}</span>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                                <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
